import React, { useState } from 'react';
import { DataTable } from '../../Nav_DataComponents/DataTable/DataTable';
import { Badge } from '../../BasicComponents/Badge/Badge';
import { Button } from '../../BasicComponents/Button/Button';
import { Modal } from '../../CompositeComponents/Modal/Modal';
import type { DeviceSessionManagerProps, Session } from './types';
import type { ColumnDef } from '../../Nav_DataComponents/DataTable/types';

// ─── DeviceSessionManager ─────────────────────────────────────────────────────

function relativeTime(ts: string) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function DeviceSessionManager({
  sessions = [],
  onRevoke,
  onRevokeAll,
  loading = false,
  className,
}: DeviceSessionManagerProps) {
  const [revoking, setRevoking] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<Session | null>(null);
  const [confirmRevokeAll, setConfirmRevokeAll] = useState<string | null>(null); // userId

  async function handleRevoke(session: Session) {
    setRevoking(session.id);
    onRevoke?.(session.id);
    setConfirmRevoke(null);
  }

  const columns: ColumnDef<Session>[] = [
    {
      key: 'userName',
      header: 'User',
      render: (v, row) => (
        <div>
          <p className="font-medium text-sm">{v as string}</p>
          <p className="text-xs text-neutral-400">{row.userEmail}</p>
        </div>
      ),
    },
    {
      key: 'deviceInfo',
      header: 'Device',
      render: (v, row) => (
        <div>
          <p className="text-xs text-neutral-700 dark:text-neutral-200 truncate max-w-[200px]">{v as string}</p>
          <p className="text-xs text-neutral-400">{row.ip}</p>
        </div>
      ),
    },
    {
      key: 'lastActive',
      header: 'Last Active',
      render: (v) => (
        <span className="text-xs text-neutral-500">{relativeTime(v as string)}</span>
      ),
    },
    {
      key: 'isCurrent',
      header: '',
      render: (v) => v ? <Badge variant="success" size="sm">Current</Badge> : null,
    },
    {
      key: 'id',
      header: '',
      render: (_v, row) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            loading={revoking === row.id}
            disabled={!!row.isCurrent}
            onClick={() => setConfirmRevoke(row)}
            aria-label={`Force logout ${row.userName} on ${row.deviceInfo}`}
          >
            Revoke
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmRevokeAll(row.userId)}
            aria-label={`Revoke all sessions for ${row.userName}`}
          >
            Revoke All
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={className}>
      <DataTable
        columns={columns}
        data={sessions}
        loading={loading}
        variant="default"
        emptyState={{ title: 'No active sessions' }}
      />

      {/* Confirm single revoke */}
      <Modal
        open={!!confirmRevoke}
        onClose={() => setConfirmRevoke(null)}
        title="Force Logout Session"
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setConfirmRevoke(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => confirmRevoke && handleRevoke(confirmRevoke)}>
              Force Logout
            </Button>
          </div>
        }
      >
        <p className="text-sm text-neutral-700 dark:text-neutral-200">
          Force logout <strong>{confirmRevoke?.userName}</strong> on <em>{confirmRevoke?.deviceInfo}</em>?
        </p>
      </Modal>

      {/* Confirm revoke all */}
      <Modal
        open={!!confirmRevokeAll}
        onClose={() => setConfirmRevokeAll(null)}
        title="Revoke All Sessions"
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setConfirmRevokeAll(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => { if (confirmRevokeAll) onRevokeAll?.(confirmRevokeAll); setConfirmRevokeAll(null); }}>
              Revoke All
            </Button>
          </div>
        }
      >
        <p className="text-sm text-neutral-700 dark:text-neutral-200">
          This will log out <strong>all devices</strong> for this user. They will need to sign in again.
        </p>
      </Modal>
    </div>
  );
}
